import { Tab, Tabs } from '@material-ui/core';
import CSS from 'csstype';
import { orderBy } from 'lodash-es';
import React from 'react';

import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { ISimpleTab } from '~/src/dash/components/tabs/common';
import {
  Col,
  Cols,
  Div,
  spacing,
  Text,
  TKey,
  usePanel,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

interface IIconAndLabelProps {
  iconName: string;
  label: string;
  color: string;
  tKey?: TKey;
}

const IconAndLabel = ({
  iconName,
  label,
  color = 'inherit',
  tKey,
}: IIconAndLabelProps): React.JSX.Element => {
  return (
    <Cols gap={spacing.small} style={{ alignItems: 'center', padding: '0 16px 0 10px' }}>
      <Col displayFlex>
        <IconV2 name={iconName} color={color} small />
      </Col>
      <Col>
        <Text small bold color={color} disableSelect tKey={tKey}>
          {label}
        </Text>
      </Col>
    </Cols>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

interface ISimpleTabsProps {
  activeTabIndex: number;
  ariaLabel: string;
  handleChangeTab: (_event: React.SyntheticEvent, newValue: number) => void;
  indicatorColor?: string;
  indicatorStyle?: CSS.Properties;
  tabs: ISimpleTab[];
  tabsStyle?: CSS.Properties;
  tabStyle?: CSS.Properties;
  wrapperStyle?: CSS.Properties;
}

// use this in conjunction with SimpleTabSections
export const SimpleTabs = ({
  activeTabIndex,
  ariaLabel,
  handleChangeTab,
  indicatorColor,
  indicatorStyle = {},
  tabs,
  tabsStyle = {},
  tabStyle = {},
  wrapperStyle = {},
}: ISimpleTabsProps): React.JSX.Element => {
  const { theme } = usePanel();
  const { t } = usePublicaTranslation();
  const tabActiveColor = indicatorColor || theme.primaryButton || publicaColors.blue;

  return (
    <Div
      style={{
        borderBottom: `1px solid ${publicaColors.lightGray}`,
        ...wrapperStyle,
      }}
    >
      <Tabs
        value={activeTabIndex}
        onChange={handleChangeTab}
        variant='scrollable'
        aria-label={`${ariaLabel} Tabs`}
        style={{
          padding: '0px',
          ...tabsStyle,
        }}
        TabIndicatorProps={{
          style: {
            color: tabActiveColor,
            backgroundColor: tabActiveColor,
            ...indicatorStyle,
          },
        }}
      >
        {orderBy(tabs, 'value').map((tab, i) => {
          const label = tab.tKey ? t(tab.tKey) : tab.label;
          return (
            <Tab
              label={
                tab.iconName ? (
                  <IconAndLabel
                    iconName={tab.iconName}
                    label={tab.label}
                    color={activeTabIndex === i ? tabActiveColor : publicaColors.veryDarkGray}
                  />
                ) : (
                  label
                )
              }
              {...a11yProps(i)}
              style={{
                color: activeTabIndex === i ? tabActiveColor : publicaColors.veryDarkGray,
                ...tabStyle,
              }}
            />
          );
        })}
      </Tabs>
    </Div>
  );
};
